import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import TollFreeNumber from "./pages/productsPages/TollFreeNumber";
import Services from "./pages/ServicesPage/Services";
import InboundSolution from "./pages/productsPages/InboundSolution";
import OutboundSolution from "./pages/productsPages/OutboundSolution";
import Did from "./pages/productsPages/Did";
import BookDemo from "./components/Bookdemo";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminPage from "./pages/AdminPage";
import ProfilePage from './components/ProfileDetail/ProfilePage'
import ForgotPassword from './pages/ForgotPassword';
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminLayout from "./pages/SuperAdminLayout";
import BuyerAdminLogin from "./pages/BuyerAdminLogin";
import BuyerAdminLayout from "./pages/BuyerAdminLayout";
import BuyerForgotPassword from "./pages/BuyerForgotPassword";

const ProtectedRoute = ({ element }) => {
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is superadmin, redirect to superadmin dashboard
  if (role === 'superadmin') {
    return <Navigate to="/superadmin/dashboard" replace />;
  }

  return element;
};

// Protected Route component for superadmin
const SuperAdminRoute = ({ children }) => {
  const superadminToken = localStorage.getItem('superadminToken');
  const superadminUser = localStorage.getItem('superadminUser');
  const location = useLocation();
  
  if (!superadminToken || !superadminUser) {
    return <Navigate to="/superadmin/login" state={{ from: location }} replace />;
  }
  
  try {
    const user = JSON.parse(superadminUser);
    if (!user || user.role !== 'superadmin') {
      return <Navigate to="/superadmin/login" state={{ from: location }} replace />;
    }
  } catch (error) {
    return <Navigate to="/superadmin/login" state={{ from: location }} replace />;
  }
  
  return children;
};

// Add this new protected route component for buyers
const BuyerProtectedRoute = ({ children }) => {
  const buyerToken = localStorage.getItem('buyerToken');
  const location = useLocation();
  
  if (!buyerToken) {
    return <Navigate to="/buyer/login" state={{ from: location }} replace />;
  }
  
  return children;
};

const RouterPage = () => {
  return (
    <Router>
      <PageContent />
    </Router>
  );
};

const PageContent = () => {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const hideHeaderAndFooterRoutes = [
    "/dashboard", 
    "/superadmin", 
    "/superadmin/login",
    "/buyer",
    "/buyer/login"
  ];

  // Early return for superadmin routes
  if (token && role === 'superadmin' && location.pathname === '/login') {
    window.location.replace('/superadmin/dashboard');
    return null;
  }

  const shouldShowHeaderAndFooter = () => {
    return !hideHeaderAndFooterRoutes.some((route) =>
      location.pathname.startsWith(route)
    );
  };

  return (
    <>
      {shouldShowHeaderAndFooter() && <Header />}
      <main className={`${shouldShowHeaderAndFooter() ? "pt-16" : ""}`}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/toll-free-number" element={<TollFreeNumber />} />
          <Route path="/services" element={<Services />} />
          <Route path="/inbound-solution" element={<InboundSolution />} />
          <Route path="/outbound-solution" element={<OutboundSolution />} />
          <Route path="/did" element={<Did />} />
          <Route path="/bookdemo" element={<BookDemo />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile-details" element={<ProfilePage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Login route with conditional redirect */}
          <Route 
            path="/login" 
            element={
              token && role === 'superadmin' 
                ? <Navigate to="/superadmin/dashboard" replace />
                : <Login />
            } 
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard/*"
            element={<ProtectedRoute element={<AdminPage />} />}
          />

          {/* Super Admin Routes */}
          <Route 
            path="/superadmin/login" 
            element={
              localStorage.getItem('superadminToken') ? 
                <Navigate to="/superadmin/dashboard" replace /> : 
                <SuperAdminLogin />
            }
          />
          
          <Route
            path="/superadmin/*"
            element={
              <SuperAdminRoute>
                <SuperAdminLayout />
              </SuperAdminRoute>
            }
          />

          {/* Redirect /superadmin to /superadmin/dashboard */}
          <Route
            path="/superadmin"
            element={<Navigate to="/superadmin/dashboard" replace />}
          />

          {/* Buyer Routes */}
          <Route 
            path="/buyer/login" 
            element={
              localStorage.getItem('buyerToken') ? 
                <Navigate to="/buyer/dashboard" replace /> : 
                <BuyerAdminLogin />
            }
          />
          
          <Route
            path="/buyer/*"
            element={
              <BuyerProtectedRoute>
                <BuyerAdminLayout />
              </BuyerProtectedRoute>
            }
          />

          {/* Redirect /buyer to /buyer/dashboard */}
          <Route
            path="/buyer"
            element={<Navigate to="/buyer/dashboard" replace />}
          />

          {/* Buyer Forgot Password */}
          <Route path="/buyer/forgot-password" element={<BuyerForgotPassword />} />

          {/* Catch all route - 404 */}
          <Route
            path="*"
            element={
              <div className="flex items-center justify-center min-h-screen">
                <h1 className="text-2xl font-bold">404 - Page Not Found</h1>
              </div>
            }
          />
        </Routes>
      </main>
      {shouldShowHeaderAndFooter() && <Footer />}
    </>
  );
};

export default RouterPage;
